import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { textsApi, languagesApi } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Import, FileText, Link as LinkIcon, Languages, Loader2, Globe, Lock } from 'lucide-react';

export default function ImportText() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importMode, setImportMode] = useState('text');

  // Check if user can import (Tier 2+ or Admin)
  const canImport = user?.is_admin || (user?.tier && user.tier >= 2);

  // URL extraction state
  const [urlInput, setUrlInput] = useState('');
  const [extracting, setExtracting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    source_language_id: '',
    target_language_id: '',
    source_url: ''  // Track if imported from URL
  });

  useEffect(() => {
    languagesApi.getAll()
      .then((res) => setLanguages(res.data))
      .catch(() => toast.error(t('common.error')));
  }, [t]);

  // Show upgrade message if user can't import
  if (!canImport) {
    return (
      <div className="container max-w-2xl mx-auto py-12 px-4">
        <Card>
          <CardContent className="py-12 text-center">
            <Lock className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('import.tier2Required')}</h2>
            <p className="text-muted-foreground mb-4">
              {t('import.tier2Message')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('import.upgradeMessage')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleExtractUrl = async () => {
    if (!urlInput.trim()) { toast.error(t('import.enterUrl')); return; }
    setExtracting(true);
    try {
      const res = await textsApi.extractFromUrl(urlInput.trim());
      setFormData((prev) => ({
        ...prev,
        title: res.data.title || '',
        content: res.data.content || '',
        source_url: urlInput.trim()  // Store the source URL
      }));
      setImportMode('text');
      toast.success(t('import.extractedWords', { count: res.data.word_count || '?' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('import.extractError'));
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error(t('import.enterTitle')); return; }
    if (!formData.content.trim()) { toast.error(t('import.enterText')); return; }
    if (!formData.source_language_id) { toast.error(t('import.selectSourceLang')); return; }

    setLoading(true);
    try {
      const createRes = await textsApi.create({
        title: formData.title.trim(),
        content: formData.content.trim(),
        source_language_id: formData.source_language_id,
        target_language_id: formData.target_language_id === 'none' ? null : formData.target_language_id || null,
        source_url: formData.source_url || null  // Pass source URL if from URL import
      });
      // Note: Backend now auto-tokenizes on creation, no need to call tokenize separately
      
      // Show different message for URL imports
      if (formData.source_url) {
        toast.success(t('import.importedToLibrary') || 'Text imported and added to public library!');
      } else {
        toast.success(t('import.importedTokenized'));
      }
      navigate(`/reader/${createRes.data.text_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('import.error'));
    } finally {
      setLoading(false);
    }
  };

  const wordCount = formData.content.trim().split(/\s+/).filter((w) => w).length;

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('import.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('import.subtitle') || 'Paste text or import from a URL to start reading'}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card data-testid="import-form-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> {t('import.newText')}
            </CardTitle>
            <CardDescription>{t('import.description')}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Import Mode Tabs */}
            <Tabs value={importMode} onValueChange={setImportMode}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="text" className="gap-2" data-testid="tab-paste">
                  <FileText className="h-4 w-4" /> {t('import.pasteText')}
                </TabsTrigger>
                <TabsTrigger value="url" className="gap-2" data-testid="tab-url">
                  <Globe className="h-4 w-4" /> {t('import.fromUrl')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="url" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>{t('import.websiteUrl')}</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder={t('import.urlPlaceholder')}
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      data-testid="import-url-input"
                    />
                    <Button
                      type="button"
                      onClick={handleExtractUrl}
                      disabled={extracting || !urlInput.trim()}
                      data-testid="extract-url-btn"
                    >
                      {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('import.extract')}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('import.extractHint')}
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="text" className="space-y-0 mt-0" />
            </Tabs>

            {/* Common fields — always rendered, visibility controlled by CSS */}
            <div style={{ display: (importMode === 'text' || formData.content) ? 'block' : 'none' }}>
              {/* Title */}
              <div className="space-y-2 mb-6">
                <Label htmlFor="title">{t('import.textTitle')}</Label>
                <Input
                  id="title"
                  placeholder={t('import.titlePlaceholder')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="input-title"
                />
              </div>

              {/* Language Selection */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="source-language">
                    <div className="flex items-center gap-2">
                      <Languages className="h-4 w-4" /> {t('import.sourceLanguage')} *
                    </div>
                  </Label>
                  <Select
                    value={formData.source_language_id}
                    onValueChange={(value) => setFormData({ ...formData, source_language_id: value })}
                  >
                    <SelectTrigger id="source-language" data-testid="select-source-language">
                      <SelectValue placeholder={t('import.selectLanguage')} />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((lang) => (
                        <SelectItem key={lang.language_id} value={lang.language_id}>
                          <span className="flex items-center gap-2">
                            {lang.name}
                            {lang.native_name && lang.native_name !== lang.name && (
                              <span className="text-muted-foreground text-sm">({lang.native_name})</span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target-language">{t('import.targetLanguage')} ({t('common.optional')})</Label>
                  <Select
                    value={formData.target_language_id}
                    onValueChange={(value) => setFormData({ ...formData, target_language_id: value })}
                  >
                    <SelectTrigger id="target-language" data-testid="select-target-language">
                      <SelectValue placeholder={t('import.selectLanguage')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('common.none')}</SelectItem>
                      {languages.map((lang) => (
                        <SelectItem key={lang.language_id} value={lang.language_id}>
                          {lang.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-between">
                  <Label htmlFor="content">{t('import.textContent')}</Label>
                  <span className="text-sm text-muted-foreground">{wordCount} {t('common.words')}</span>
                </div>
                <Textarea
                  id="content"
                  placeholder={t('import.pasteText')}
                  className="min-h-[300px] font-reader text-base leading-relaxed"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  data-testid="input-content"
                />
              </div>

              {/* URL Import Notice */}
              {formData.source_url && (
                <div className="mb-6 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
                  <Globe className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600 dark:text-green-400">
                    This text will be automatically added to the public library for others to discover.
                  </span>
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => navigate('/dashboard')} disabled={loading}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={loading} className="gap-2" data-testid="submit-import-btn">
                  {loading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> {t('import.importing')}</>
                  ) : (
                    <><Import className="h-4 w-4" /> {t('import.importAndStart')}</>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
